import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { upsertPhoneOptions, uploadPhoneBill } from '@services/cpqService';
import { useCheckoutStore } from '@store/checkoutStore';
import type { PhoneFormValues, PhoneNumberOptionsRequest, PhoneNumberOptionsResponse } from '@t/checkout.types';

const buildPayload = (
  values: PhoneFormValues,
  uploadedFiles: PhoneNumberOptionsRequest['phoneBillFiles']
): PhoneNumberOptionsRequest => {
  const isPortIn = values.choice === 'PORTIN';
  const isOtherProvider = values.portInProvider === 'Other';

  return {
    selectedOption: values.choice,
    existingPhoneNumber: isPortIn ? values.portInNumber : undefined,
    serviceProvider: isPortIn ? (isOtherProvider ? 'Other' : values.portInProvider) : undefined,
    providerName: isPortIn && isOtherProvider ? values.portInProviderOther : undefined,
    accountNumber: isPortIn ? values.accountNumber : undefined,
    nameOnBill: isPortIn ? values.nameOnBill : undefined,
    directoryListingOption: values.directoryListing,
    phoneBillFiles: uploadedFiles && uploadedFiles.length > 0 ? uploadedFiles : undefined,
  };
};

export const useSavePhoneInfo = (sessionId: string | null) => {
  const setPhoneInfo = useCheckoutStore((s) => s.setPhoneInfo);
  const completeStep = useCheckoutStore((s) => s.completeStep);

  const mutation = useMutation<PhoneNumberOptionsResponse, Error, PhoneFormValues>({
    mutationFn: async (values) => {
      const uploadedFiles = await Promise.all(values.phoneBillFiles.map((file) => uploadPhoneBill(sessionId!, file)));
      const mergedFiles = [...values.existingPhoneBillFiles, ...uploadedFiles];
      return upsertPhoneOptions(sessionId!, buildPayload(values, mergedFiles));
    },
    onSuccess: (_, values) => {
      setPhoneInfo(values);
      completeStep('phone');
    },
    onError: () => {
      toast.error('Failed to save phone options. Please try again.');
    },
  });

  return {
    save: mutation.mutate,
    isSaving: mutation.isPending,
  };
};
